import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import styled from 'styled-components';
import { Helmet } from 'react-helmet';

// Language detection system
import { LanguageProvider } from './contexts/LanguageContext';
import GoogleTranslateBlocker from './components/GoogleTranslateBlocker';

// Silent auto-update service
import { silentUpdateService } from './services/silentUpdateService';

// ✅ ADDED: Firebase foreground notification handler
import { onForegroundMessage, showLocalNotification } from './services/firebaseService';

// ✅ ADDED: Third-party lazy loader
import getThirdPartyLoader from './utils/thirdPartyLoader';

// Component imports
import FeedRouter from './components/FeedRouter';
import SimpleNotificationButton from './components/SimpleNotificationButton';
import PWAInstallButton from './components/PWAInstallButton';
import SinglePostFeed from './components/SinglePostFeed';
import Header from './components/Header';
import Footer from './components/Footer';
import AboutPage from './components/pages/AboutPage';
import PrivacyPage from './components/pages/PrivacyPage';
import TermsPage from './components/pages/TermsPage';
import ContactPage from './components/pages/ContactPage';
import TagPage from './components/pages/TagPage';
import RelatedPage from './components/pages/RelatedPage';
import SwipeProvider, { useSwipe } from './components/SwipeProvider';
import AdSenseScript from './components/AdSenseScript';

// Styled components
const AppContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const MainContent = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

// Mobile detection function
const isMobile = () => {
  if (typeof window === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768;
};

// Desktop layout wrapper for news pages
const DesktopWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

// Mobile layout wrapper (no header/footer for immersive experience)
const MobileWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

// Page wrapper for static pages
const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

// Placeholder component for pages under development
const PlaceholderPage: React.FC<{ title: string; description: string }> = ({ title, description }) => (
  <>
    <Helmet>
      <title>{title} - Infonium | Under Development</title>
      <meta name="description" content={`${description} The ${title} page is being developed to provide the best experience for Infonium users.`} />
      <meta name="keywords" content={`${title.toLowerCase()}, infonium, under development, coming soon, development`} />
      <meta name="author" content="Infonium" />
      <meta property="og:title" content={`${title} - Infonium`} />
      <meta property="og:description" content={`${description} The ${title} page is under development.`} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={typeof window !== 'undefined' ? window.location.href : 'https://www.infonium.org/categories'} />
      <meta property="og:image" content="https://cdn.infonium.org/img/logo_infonium.png" />
      <meta property="og:site_name" content="Infonium" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={`${title} - Infonium`} />
      <meta name="twitter:description" content={`${description} The ${title} page is under development.`} />
      <meta name="twitter:image" content="https://cdn.infonium.org/img/logo_infonium.png" />
      <meta name="twitter:site" content="@infonium" />
      <link rel="canonical" href={typeof window !== 'undefined' ? window.location.href : 'https://www.infonium.org/categories'} />
      <meta name="robots" content="index, follow" />
      <meta name="language" content="en" />
    </Helmet>
  <div style={{ 
    padding: '60px 20px', 
    textAlign: 'center', 
    minHeight: '60vh',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
  }}>
    <div style={{
      background: 'white',
      padding: '40px',
      borderRadius: '20px',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
      maxWidth: '600px'
    }}>
      <h1 style={{ 
        fontSize: '36px', 
        color: '#2c3e50', 
        marginBottom: '20px',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent'
      }}>
        {title}
      </h1>
      <p style={{ 
        fontSize: '18px', 
        color: '#34495e', 
        lineHeight: '1.6' 
      }}>
        {description}
      </p>
      <div style={{ 
        marginTop: '30px',
        fontSize: '48px'
      }}>
        🚧
      </div>
    </div>
  </div>
  </>
);

// Mobile app wrapper with swipe functionality
const MobileAppContent: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isRelatedPageVisible, hideRelatedPage } = useSwipe();
  
  return (
    <>
      {children}
      <RelatedPage 
        isVisible={isRelatedPageVisible} 
        onClose={hideRelatedPage} 
      />
    </>
  );
};

const App: React.FC = () => {
  const [isMobileView, setIsMobileView] = useState(isMobile());

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(isMobile());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 🚀 SILENT AUTO-UPDATE: DISABLED (Using 302 redirects instead)
  useEffect(() => {
    // Service worker updates disabled - using seamless 302 redirects instead
    console.log('🚀 Service worker updates disabled - using 302 redirect system for seamless updates');
  }, []);

  // ✅ ADDED: Global foreground notification handler
  useEffect(() => {
    console.log('🔔 Setting up global foreground notification listener...');
    
    let unsubscribe: (() => void) | null = null;
    
    try {
      unsubscribe = onForegroundMessage((payload) => {
        console.log('📨 Global foreground notification received:', payload);
        
        // Always show notification when app is in foreground
        const title = payload.notification?.title || 'Infonium';
        const body = payload.notification?.body || 'You have a new notification';
        const icon = payload.notification?.icon || '/icons/icon-192x192.png';
        
        // Show local notification
        showLocalNotification(title, body, icon);
        
        // Optional: Show toast or in-app notification
        console.log(`🔔 Displayed foreground notification: ${title}`);
      });
      
      console.log('✅ Global foreground notification listener setup complete');
    } catch (error) {
      console.error('❌ Failed to setup foreground notification listener:', error);
    }
    
    // Cleanup on unmount
    return () => {
      if (unsubscribe) {
        try {
          unsubscribe();
          console.log('🧹 Global foreground notification listener cleaned up');
        } catch (error) {
          console.error('❌ Error cleaning up foreground listener:', error);
        }
      }
    };
  }, []);

  // 🚀 ADDED: Third-party scripts lazy loading
  useEffect(() => {
    console.log('📊 [App] Initializing third-party lazy loader...');
    
    // Initialize third-party loader
    const thirdPartyLoader = getThirdPartyLoader();
    
    // Signal that React app is ready after a short delay
    const signalReactReady = () => {
      const event = new CustomEvent('react-app-ready');
      window.dispatchEvent(event);
      console.log('⚛️ [App] React app ready signal dispatched');
    };
    
    // Wait for initial render to complete
    const timeoutId = setTimeout(signalReactReady, 500);
    
    // Cleanup
    return () => {
      clearTimeout(timeoutId);
      thirdPartyLoader.cleanup();
      console.log('🧹 [App] Third-party loader cleaned up');
    };
  }, []);

  return (
    <LanguageProvider>
      <GoogleTranslateBlocker enableLogging={process.env.NODE_ENV === 'development'}>
        <Router>
          <SwipeProvider>
            <AppContainer>
            {/* 📱 Load Google AdSense script globally */}
            <AdSenseScript />
        
        <Helmet>
          <title>Infonium - Modern Information Platform</title>
          <meta name="description" content="Infonium là nền tảng thông tin hiện đại, cung cấp tin tức và nội dung chất lượng cao với trải nghiệm người dùng tối ưu." />
          <meta name="keywords" content="infonium, tin tức, thông tin, báo chí, nội dung, media, articles, news" />
          <meta name="author" content="Infonium" />
          <meta property="og:title" content="Infonium - Modern Information Platform" />
          <meta property="og:description" content="Nền tảng thông tin hiện đại với trải nghiệm người dùng tối ưu" />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={typeof window !== 'undefined' ? window.location.href : ''} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Infonium - Modern Information Platform" />
          <meta name="twitter:description" content="Nền tảng thông tin hiện đại với trải nghiệm người dùng tối ưu" />
          <link rel="canonical" href={typeof window !== 'undefined' ? window.location.href : ''} />
          <meta name="viewport" content="width=device-width, initial-scale=1.0, viewport-fit=cover" />
          <meta name="theme-color" content="#667eea" />
        </Helmet>

        <Routes>
          {/* Main feed routes */}
          <Route 
            path="/" 
            element={
              isMobileView ? (
                <MobileAppContent>
                  <MobileWrapper>
                    <FeedRouter />
                  </MobileWrapper>
                </MobileAppContent>
              ) : (
                <DesktopWrapper>
                  <Header />
                  <MainContent>
                    <FeedRouter />
                  </MainContent>
                  <Footer />
                </DesktopWrapper>
              )
            } 
          />
          
          <Route 
            path="/post/:id/:slug.html" 
            element={
              isMobileView ? (
                <MobileAppContent>
                  <MobileWrapper>
                    <FeedRouter />
                  </MobileWrapper>
                </MobileAppContent>
              ) : (
                <SinglePostFeed />
              )
            } 
          />

          {/* Legacy route support */}
          <Route 
            path="/post/:slug" 
            element={
              isMobileView ? (
                <MobileAppContent>
                  <MobileWrapper>
                    <FeedRouter />
                  </MobileWrapper>
                </MobileAppContent>
              ) : (
                <SinglePostFeed />
              )
            } 
          />

          {/* Tag pages - WordPress style slug-only URLs */}
          <Route 
            path="/tag/:slug/post/:postId" 
            element={
              <MobileAppContent>
                <MobileWrapper>
                  <TagPage />
                </MobileWrapper>
              </MobileAppContent>
            } 
          />
          
          <Route 
            path="/tag/:slug" 
            element={
              <MobileAppContent>
                <MobileWrapper>
                  <TagPage />
                </MobileWrapper>
              </MobileAppContent>
            } 
          />

          {/* Legacy tag routes with ID for backward compatibility */}
          <Route 
            path="/tag/:id/:slug" 
            element={
              <MobileAppContent>
                <MobileWrapper>
                  <TagPage />
                </MobileWrapper>
              </MobileAppContent>
            } 
          />

          {/* Essential pages for AdSense compliance */}
          <Route 
            path="/about" 
            element={
              <PageWrapper>
                <Header />
                <MainContent>
                  <AboutPage />
                </MainContent>
                <Footer />
              </PageWrapper>
            } 
          />

          <Route 
            path="/privacy" 
            element={
              <PageWrapper>
                <Header />
                <MainContent>
                  <PrivacyPage />
                </MainContent>
                <Footer />
              </PageWrapper>
            } 
          />

          <Route 
            path="/terms" 
            element={
              <PageWrapper>
                <Header />
                <MainContent>
                  <TermsPage />
                </MainContent>
                <Footer />
              </PageWrapper>
            } 
          />

          <Route 
            path="/contact" 
            element={
              <PageWrapper>
                <Header />
                <MainContent>
                  <ContactPage />
                </MainContent>
                <Footer />
              </PageWrapper>
            } 
          />

          {/* Additional pages for comprehensive website structure */}
          <Route 
            path="/categories" 
            element={
              <PageWrapper>
                <Header />
                <MainContent>
                  <PlaceholderPage 
                    title="Chuyên mục" 
                    description="Khám phá các chuyên mục tin tức đa dạng của chúng tôi. Trang này đang được phát triển để mang đến trải nghiệm tốt nhất." 
                  />
                </MainContent>
                <Footer />
              </PageWrapper>
            } 
          />

          {/* 404 page - specific route - redirect to homepage */}
          <Route 
            path="/404" 
            element={
              <Navigate to="/" replace />
            } 
          />

          {/* 404 page - catch all - redirect to homepage */}
          <Route 
            path="*" 
            element={
              <Navigate to="/" replace />
            } 
          />
        </Routes>
        
        {/* 🔔 Global PWA Install Button - Always visible on mobile */}
        <PWAInstallButton />
        
        {/* 🔔 Global Notification Button - Always visible on mobile */}
        <SimpleNotificationButton 
          visible={isMobileView} 
          onSubscriptionChange={(subscribed) => {
            console.log('📱 Notification subscription changed:', subscribed);
          }}
        />
        
        </AppContainer>
      </SwipeProvider>
        </Router>
      </GoogleTranslateBlocker>
    </LanguageProvider>
  );
};

export default App;
